import {
  AppBar,
  Box,
  Button,
  Drawer,
  Step,
  StepLabel,
  Stepper,
  Toolbar,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router-dom';
import { IConfig } from '../../../../Interfaces/IConfig';
import { IDocument } from '../../../../Interfaces/IDocument';
import { getEmptyIMortgage, IMortgage } from '../../../../Interfaces/IMortgage';
import { IPerson } from '../../../../Interfaces/IPerson';
import { IPersonHasDocument } from '../../../../Interfaces/IPersonHasDocument';
import { ConfirmNavigateBackDialog } from '../../../core/ConfirmNavigateBackDialog';
import { CustomeSlide } from '../../../core/CustomeSlide';
import Send from '../../../generic/Send';
import { RestoreProgress } from '../RestoreProgress';
import { SaveProgress } from '../SaveProgress';
import { MortageBaseInformation } from './Childs/Dialog/MortageBaseInformation';
import { MortgageBeneficiary } from './Childs/Dialog/MortgageBeneficiary';
import { MortgageContact } from './Childs/Dialog/MortgageContact';
import { MortgageDocuments } from './Childs/Dialog/MortgageDocuments';
import { MortgageGeneral } from './Childs/Dialog/MortgageGeneral';
import { MortgageLandRegisterData } from './Childs/Dialog/MortgageLandRegisterData';
import { MortgageOptions } from './Childs/Dialog/MortgageOptions';
import { MortgageSummary } from './Childs/Dialog/MortgageSummary';
import { Start } from './Childs/Dialog/Start';

interface IProps {
  configObject: IConfig;
}

export const MortgageDialog: React.FC<IProps> = ({ configObject }) => {
  const navigate = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [lastCurrentStep, setLastCurrentStep] = useState(0);
  const [activeStepCounter, setActiveStepCounter] = useState(0);
  const [txt, setTxt] = useState<React.ReactNode>(<></>);

  const [mortgageObject, setMortgageObject] =
    useState<IMortgage>(getEmptyIMortgage);

  const [personArray, setPersonArray] = useState<IPerson[]>([]);
  const [mortgageDocumentArray, setMortgageDocumentArray] = useState<
    IDocument[]
  >([]);
  const [documentArray, setDocumentArray] = useState<IDocument[]>([]);
  const [personHasDocument, setPersonHasDocument] = useState<
    IPersonHasDocument[]
  >([]);

  const [isDebugEnabled, setIsDebugEnabled] = useState(false);

  useHotkeys('shift+d', () => setIsDebugEnabled(!isDebugEnabled));

  /* ###############################  BEGIN Browser-Action-Control: <- and -> ############################### */
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      setConfirmDialogOpen(true);
      // neuen State pushen, damit die Pfeiltaste erneut funktioniert
      window.history.pushState(null, '', window.location.href);
    };

    // initialen State pushen
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleCancel = () => {
    setConfirmDialogOpen(false);
    window.history.pushState(null, '', window.location.href);
  };

  const handleConfirm = () => {
    setConfirmDialogOpen(false);
    navigate('/');
  };

  /* ###############################  BEGIN SAVE AND RESTORE ############################### */
  const getDataToSave = () => {
    const tempDataToSave = {
      CurrentStep: currentStep,
      LastCurrentStep: lastCurrentStep,
      ActiveStepCounter: activeStepCounter,
      MortgageObject: mortgageObject,
      PersonArray: personArray,
      PersonHasDocument: personHasDocument,
      DocumentArray: documentArray,
      MortgageDocumentArray: mortgageDocumentArray,
    };
    return tempDataToSave;
  };

  const restoreSetterFunction = (uplodatedFile: any) => {
    if ('CurrentStep' in uplodatedFile) {
      setCurrentStep(uplodatedFile['CurrentStep']);
    }
    if ('LastCurrentStep' in uplodatedFile) {
      setLastCurrentStep(uplodatedFile['LastCurrentStep']);
    }
    if ('ActiveStepCounter' in uplodatedFile) {
      setActiveStepCounter(uplodatedFile['ActiveStepCounter']);
    }
    if ('Txt' in uplodatedFile) {
      setTxt(uplodatedFile['Txt']);
    }
    if (
      'MortgageObject' in uplodatedFile &&
      uplodatedFile['MortgageObject'] !== mortgageObject
    ) {
      setMortgageObject(uplodatedFile['MortgageObject']);
    }
    if (
      'PersonArray' in uplodatedFile &&
      uplodatedFile['PersonArray'] !== personArray
    ) {
      setPersonArray(uplodatedFile['PersonArray']);
    }
    if (
      'PersonHasDocument' in uplodatedFile &&
      uplodatedFile['PersonHasDocument'] !== personHasDocument
    ) {
      setPersonHasDocument(uplodatedFile['PersonHasDocument']);
    }
    if (
      'DocumentArray' in uplodatedFile &&
      uplodatedFile['DocumentArray'] !== personHasDocument
    ) {
      setDocumentArray(uplodatedFile['DocumentArray']);
    }
    if (
      'MortgageDocumentArray' in uplodatedFile &&
      uplodatedFile['MortgageDocumentArray'] !== personHasDocument
    ) {
      setMortgageDocumentArray(uplodatedFile['MortgageDocumentArray']);
    }
  };

  /* ###############################  END SAVE AND RESTORE ############################### */

  const handleNext = (nextPage?: number) => {
    setLastCurrentStep(currentStep);

    if (
      (nextPage === undefined ? currentStep + 1 : nextPage) === 4 &&
      mortgageDocumentArray.length > 0
    ) {
      setCurrentStep(8);
    } else if (
      mortgageObject.ToBeDeleted &&
      (nextPage === undefined ? currentStep + 1 : nextPage) === 5
    ) {
      setCurrentStep(6);
    } else {
      // Normaler Ablauf
      if (nextPage === undefined) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentStep(nextPage);
      }
    }
  };
  const handleBack = () => {
    const tmpStep = currentStep;

    if (mortgageObject.ToBeDeleted && tmpStep === 6) {
      setCurrentStep(4);
    } else {
      // Normaler Rückwärts-Navigationslogik
      if (tmpStep - lastCurrentStep > 1) {
        setCurrentStep(lastCurrentStep);
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
    setLastCurrentStep(currentStep);
  };

  useEffect(() => {
    if (mortgageDocumentArray.length > 0) {
      setMortgageObject({ ...mortgageObject, BeneficiaryDetails: null });
    }
  }, [mortgageDocumentArray]);

  return (
    <>
      <ConfirmNavigateBackDialog
        open={confirmDialogOpen}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />

      <Box sx={{ display: 'flex' }}>
        <Drawer
          sx={{
            width: 250,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 250,
              boxSizing: 'border-box',
            },
          }}
          variant='persistent'
          anchor='left'
          open={true}
        >
          <Box sx={{ p: 2, display: 'flex' }}>
            <Stepper activeStep={currentStep} orientation='vertical'>
              <Step key={`mortgage-0`} completed={currentStep > 0}>
                <StepLabel>Grundschuld</StepLabel>
              </Step>
              <Step key={`mortgage-1`} completed={currentStep > 1}>
                <StepLabel>Eigentümer</StepLabel>
              </Step>
              <Step key={`mortgage-2`} completed={currentStep > 2}>
                <StepLabel>Bestellen / Löschen</StepLabel>
              </Step>
              <Step key={`mortgage-3`} completed={currentStep > 3}>
                <StepLabel>Grundschulddokument(e)</StepLabel>
              </Step>
              <Step key={`mortgage-3`} completed={currentStep > 4}>
                <StepLabel>Grundbuchdaten</StepLabel>
              </Step>
              <Step key={`mortgage-3`} completed={currentStep > 5}>
                <StepLabel>Grundpfand</StepLabel>
              </Step>
              <Step key={`mortgage-4`} completed={currentStep > 6}>
                <StepLabel>Gläubiger</StepLabel>
              </Step>
              <Step key={`mortgage-5`} completed={currentStep > 7}>
                <StepLabel>Dokument(e)</StepLabel>
              </Step>
              <Step key={`mortgage-6`} completed={currentStep > 8}>
                <StepLabel>Zusammenfassung</StepLabel>
              </Step>
            </Stepper>
          </Box>
        </Drawer>

        <Box sx={{ flexGrow: 1, width: { sm: `calc(100% - ${250}px)` } }}>
          <AppBar position='static'>
            <Toolbar>
              <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
                Formular zur Grundschuld
              </Typography>
              <RestoreProgress
                key='mortgage-import'
                setterFunction={restoreSetterFunction}
              ></RestoreProgress>
              <SaveProgress
                key='mortgage-export'
                idLegaltransaction={100}
                getDataToSave={getDataToSave}
                filename={'Grundschuldantrag'}
              ></SaveProgress>
            </Toolbar>
          </AppBar>

          <Box sx={{ p: 3 }}>
            <img
              width={250}
              src={`data:${configObject.logo_mime};base64,${configObject.logo}`}
            />

            <CustomeSlide
              currentPage={currentStep}
              pageNumber={0}
              lastCurrentPage={lastCurrentStep}
              appear={false}
            >
              <Start
                currentStep={currentStep}
                setcurrentStep={setCurrentStep}
                handleNext={handleNext}
              />
            </CustomeSlide>

            <CustomeSlide
              currentPage={currentStep}
              pageNumber={1}
              lastCurrentPage={lastCurrentStep}
            >
              <MortageBaseInformation
                personArray={personArray}
                setPersonArray={setPersonArray}
                personHasDocument={personHasDocument}
                setPersonHasDocument={setPersonHasDocument}
                mortgageObject={mortgageObject}
                setMortgageObject={setMortgageObject}
                handleBack={handleBack}
                handleNext={handleNext}
              />
            </CustomeSlide>

            <CustomeSlide
              currentPage={currentStep}
              pageNumber={2}
              lastCurrentPage={lastCurrentStep}
            >
              <MortgageOptions
                configObject={configObject}
                mortgageObject={mortgageObject}
                setMortgageObject={setMortgageObject}
                handleBack={handleBack}
                handleNext={handleNext}
              />
            </CustomeSlide>

            <CustomeSlide
              currentPage={currentStep}
              pageNumber={3}
              lastCurrentPage={lastCurrentStep}
            >
              <MortgageDocuments
                title='Grundschulddokument(e)'
                setMortgageObject={setMortgageObject}
                mortgageObject={mortgageObject}
                documentArray={mortgageDocumentArray}
                setDocumentArray={setMortgageDocumentArray}
                handleBack={handleBack}
                handleNext={handleNext}
                getMortgageDocuments
              />
            </CustomeSlide>

            <CustomeSlide
              currentPage={currentStep}
              pageNumber={4}
              lastCurrentPage={lastCurrentStep}
            >
              <MortgageLandRegisterData
                configObject={configObject}
                handleBack={handleBack}
                handleNext={handleNext}
                mortgageObject={mortgageObject}
                setMortgageObject={setMortgageObject}
              />
            </CustomeSlide>

            <CustomeSlide
              currentPage={currentStep}
              pageNumber={5}
              lastCurrentPage={lastCurrentStep}
            >
              <MortgageGeneral
                setMortgageObject={setMortgageObject}
                mortgageObject={mortgageObject}
                handleBack={handleBack}
                handleNext={handleNext}
              />
            </CustomeSlide>

            <CustomeSlide
              currentPage={currentStep}
              pageNumber={6}
              lastCurrentPage={lastCurrentStep}
            >
              <MortgageBeneficiary
                setMortgageObject={setMortgageObject}
                mortgageObject={mortgageObject}
                handleBack={handleBack}
                handleNext={handleNext}
              />
            </CustomeSlide>

            <CustomeSlide
              currentPage={currentStep}
              pageNumber={7}
              lastCurrentPage={lastCurrentStep}
            >
              <MortgageDocuments
                title='Weitere Dokumente'
                setMortgageObject={setMortgageObject}
                mortgageObject={mortgageObject}
                documentArray={documentArray}
                setDocumentArray={setDocumentArray}
                handleBack={handleBack}
                handleNext={handleNext}
              />
            </CustomeSlide>

            <CustomeSlide
              currentPage={currentStep}
              pageNumber={8}
              lastCurrentPage={lastCurrentStep}
            >
              <>
                <MortgageSummary
                  mortgageObject={mortgageObject}
                  setMortgageObject={setMortgageObject}
                  personArray={personArray}
                  documentArray={documentArray}
                />

                <MortgageContact
                  documentArray={documentArray}
                  mortgageDocumentArray={mortgageDocumentArray}
                  mortgageObject={mortgageObject}
                  setMortgageObject={setMortgageObject}
                  handleBack={handleBack}
                  handleNext={handleNext}
                  personArray={personArray}
                  personHasDocument={personHasDocument}
                  configObject={configObject}
                />
              </>
            </CustomeSlide>

            <CustomeSlide
              currentPage={currentStep}
              pageNumber={9}
              lastCurrentPage={lastCurrentStep}
            >
              <Send />
            </CustomeSlide>

            {isDebugEnabled && (
              <>
                <b>DEBUG</b>
                <br />
                {`currentPage: ${currentStep}`}
                <br />
                <Button onClick={() => setCurrentStep(1)}>
                  Zurück zum Start
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Drawer
          sx={{
            width: 250,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 250,
              boxSizing: 'border-box',
            },
          }}
          variant='persistent'
          anchor='right'
          open={true}
        >
          <Box sx={{ p: 2 }}>{txt}</Box>
        </Drawer>
      </Box>
    </>
  );
};
